import React from 'react';
import Layout from '../../Layout/Layout';
import { motion } from 'framer-motion';

const Solution = () => {
  // Simplified animation variants for Framer Motion v4
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { 
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };
  
  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { 
      y: 0, 
      opacity: 1,
      transition: { duration: 0.5 }
    }
  };

  // Solution cards data
  const solutionCards = [
    {
      icon: "💱",
      title: "Decentralized Exchange",
      description: "Trade cryptocurrencies directly from your wallet with no intermediaries. Lower fees, higher security.",
      features: ["Lowest gas fees", "Cross-chain swaps", "Liquidity pools"],
      color: "bg-blue-600",
      lightColor: "bg-blue-100"
    },
    {
      icon: "🔐",
      title: "Secure Wallet",
      description: "Keep your crypto assets safe with our state-of-the-art security protocols and multi-signature technology.",
      features: ["Multi-factor authentication", "Cold storage", "Transaction monitoring"],
      color: "bg-indigo-600",
      lightColor: "bg-indigo-100"
    },
    {
      icon: "📊",
      title: "Advanced Analytics",
      description: "Make informed decisions with real-time market insights, technical analysis tools, and portfolio tracking.",
      features: ["Customizable dashboards", "Price alerts", "Trend indicators"],
      color: "bg-cyan-600",
      lightColor: "bg-cyan-100"
    },
    {
      icon: "🤖",
      title: "Trading Bots",
      description: "Automate your crypto trading strategies with customizable bots that execute trades based on your parameters.",
      features: ["Strategy backtesting", "Risk management", "24/7 operation"],
      color: "bg-purple-600",
      lightColor: "bg-purple-100"
    }
  ];

  return (
    <div>
      <Layout>
        <main className="w-full min-h-screen bg-blue-50">
          {/* Header Section */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center py-16 px-4 bg-white shadow-md"
          >
            <h1 className="text-blue-950 font-bold text-3xl md:text-4xl lg:text-5xl mb-6">
              Crypto Solutions
            </h1>
            <p className="text-gray-700 max-w-3xl mx-auto text-lg">
              Powerful tools and services designed to help you navigate the crypto markets with confidence
            </p>
          </motion.div>

          {/* Solutions Grid */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="container mx-auto px-4 py-16"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {solutionCards.map((card, index) => (
                <motion.div
                  key={index}
                  variants={itemVariants}
                  whileHover={{ y: -5 }}
                  className="bg-white rounded-xl overflow-hidden shadow-lg transform transition-all duration-300 hover:shadow-xl"
                >
                  {/* Card header with colored background */}
                  <div className={`${card.color} py-6 px-6 flex items-center`}>
                    <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center mr-4 shadow-md">
                      <span className="text-2xl">{card.icon}</span>
                    </div>
                    <h3 className="text-white font-bold text-xl">{card.title}</h3>
                  </div>
                  
                  {/* Card content */}
                  <div className="p-6">
                    <p className="text-gray-600 mb-6">{card.description}</p>
                    
                    <div className="space-y-3">
                      {card.features.map((feature, i) => (
                        <div key={i} className="flex items-center">
                          <div className={`w-6 h-6 rounded-full ${card.color} flex items-center justify-center flex-shrink-0`}>
                            <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                            </svg>
                          </div>
                          <span className="ml-3 text-gray-700">{feature}</span>
                        </div>
                      ))}
                    </div>
                    
                    <button className={`mt-8 w-full py-3 px-4 rounded-lg ${card.color} text-white font-medium shadow-md hover:shadow-lg transition-shadow duration-300`}>
                      Learn More
                    </button>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Features Section */}
          <div className="bg-blue-900 text-white py-16">
            <div className="container mx-auto px-4">
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 1 }}
                className="text-center mb-12"
              >
                <h2 className="text-3xl font-bold mb-4">Why Choose Our Platform?</h2>
                <div className="h-1 w-24 bg-blue-400 mx-auto"></div>
              </motion.div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {[
                  { title: "Security First", description: "Enterprise-grade security measures to protect your assets", icon: "🔒" },
                  { title: "Low Fees", description: "Competitive fee structure designed to maximize your profits", icon: "💰" },
                  { title: "24/7 Support", description: "Our expert team is always available to assist you", icon: "🌐" }
                ].map((item, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.2, duration: 0.5 }}
                    className="bg-blue-800 rounded-xl p-6 text-center shadow-lg"
                  >
                    <div className="bg-blue-700 h-16 w-16 rounded-full flex items-center justify-center mx-auto mb-6">
                      <div className="text-3xl">{item.icon}</div>
                    </div>
                    <h3 className="text-xl font-semibold mb-3">{item.title}</h3>
                    <p className="text-blue-100">{item.description}</p>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>

          {/* Stats Section */}
          <div className="py-16 bg-white">
            <div className="container mx-auto px-4">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
                {[
                  { label: "Trading Volume", value: "$2.4B", icon: "📊" },
                  { label: "Active Users", value: "85,000+", icon: "👥" },
                  { label: "Supported Coins", value: "120+", icon: "🪙" },
                  { label: "Countries", value: "160+", icon: "🌎" }
                ].map((stat, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: i * 0.1, duration: 0.5 }}
                    className="bg-blue-50 rounded-xl p-6 text-center shadow-md"
                  >
                    <div className="text-4xl mb-4">{stat.icon}</div>
                    <div className="text-3xl font-bold text-blue-900 mb-2">{stat.value}</div>
                    <div className="text-gray-600">{stat.label}</div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>

         
        </main>
      </Layout>
    </div>
  );
};

export default Solution;